import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import Typography from 'Components/UI/Typography';
import { commaSeparated, currency } from 'Helpers/Formats';
import { Skeleton } from '@material-ui/lab';
import useMobileLayout from 'Hooks/useMobileLayout';
import HoldingCard from 'Components/UI/HoldingCard/HoldingCard';
import { Box } from '@material-ui/core';
import fr from 'facet-rest';

const SectionLabel = ({
  classes, color, label, value, type, percentageValue, usePercentage,
}) => {
  const displayValue = usePercentage ? percentageValue : currency(value);
  return (
    <div className={classes.tableRow}>
      <FiberManualRecordIcon style={{ color }} />
      <Typography variant="p" noWrap color="black">
        {`${type} - ${displayValue} ${label}`}
      </Typography>
    </div>
  );
};

SectionLabel.defaultProps = {
  usePercentage: false,
  percentageValue: '',
};

SectionLabel.propTypes = {
  classes: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  usePercentage: PropTypes.bool,
  percentageValue: PropTypes.string,
};

const defaultFormat = value => value;

// Temporarily commented out because we are displaying incorrect values from Apex 4/20/22
// const gainLossFormat = value => {
//   if (!value.includes('-')) {
//     return `+${value}`;
//   }
//   return `${value}`;
// };

const cells = [
  {
    format: defaultFormat,
    attrName: 'ticker',
    label: 'Symbol',
  },
  {
    format: commaSeparated,
    attrName: 'quantity',
    label: 'Quantity',
  },
  {
    format: currency,
    attrName: 'currentPrice',
    label: 'Price',
  },
  // Temporarily commented out because we are displaying incorrect values from Apex 4/20/22
  // {
  //   format: currency,
  //   attrName: 'costBasis',
  //   label: 'Cost Basis',
  // },
  // {
  //   format: gainLossFormat,
  //   attrName: 'percentGain',
  //   label: 'Gain/Loss(%)',
  // },
  {
    format: currency,
    attrName: 'currentValue',
    label: 'Value',
  },
];

function ChartLegend({
  classes,
  className,
  sections,
  accountCollection,
  noColumnHeaders,
  usePercentage,
  noBreakdowns,
  sectionLabels,
}) {
  const isMobile = useMobileLayout();
  if (!accountCollection) {
    return Array(3)
      .fill()
      .map(() => <Skeleton className={classes.tableRow} />);
  }
  const holdings = noBreakdowns ? null : [...(accountCollection || [])]
    .map(acc => [...(acc.attr('holdings') || [])])
    .flat();

  const columnHeaders = () => {
    return (
      <div className={classes.headerRow}>
        {cells.map(({ label }) => (
          <Typography
            className={classNames(classes.cell, classes.header, {
              [classes.alignTextEnd]: label === 'Current Value',
            })}
            variant="body1"
            color="primary"
            noWrap
            title={label}
          >
            {label}
          </Typography>
        ))}
      </div>);
  };
  const showHeaders = !isMobile && !noColumnHeaders;
  return (
    <Box
      className={classNames(classes.root, className)}
      test="holdings-chart-legend"
    >
      {showHeaders && columnHeaders()}
      {sections.map((sectionProps) => {
        return (
          <div className={classNames(classes.sectionRoot, {
            [classes.sectionWithoutBreakdown]: noBreakdowns,
          })}
          >
            {sectionLabels && <SectionLabel {...sectionProps} classes={classes} usePercentage={usePercentage} />}
            {holdings?.filter((h) => {
              const fields = h
                .meta()
                .field('tickerClassification')
                .options();

              const found = fields.findItem((i) => {
                return i.attr('id') === h.attr('tickerClassification');
              });

              return found?.attr('prompt') === sectionProps.label;
            })
              .map(data => {
                if (isMobile) {
                  return (
                    <HoldingCard
                      key={data.id()}
                      holdingResource={data}
                      holdingClassificationColor={sectionProps.color}
                      expandedByDefault={sections.length === 1}
                    />);
                }
                return (
                  <div className={classes.tableRow} key={data.id()}>
                    {cells.map(({ attrName, format }) => {
                      return attrName === 'ticker'
                        ? (
                          <div className={classNames(classes.cell, classes.cellContainer)}>
                            <FiberManualRecordIcon style={{ color: sectionProps.color }} />
                            <Typography
                              className={classes.tickerCellText}
                              key={attrName}
                              variant="h6"
                              color="primary"
                              noWrap
                              title={data.attr(attrName)}
                            >
                              {format(data.attr(attrName))}
                            </Typography>
                          </div>
                        )
                        : (
                          <Typography
                            key={attrName}
                            className={classNames(classes.cell, classes.cellText, {
                              [classes.alignTextEnd]: attrName === 'currentValue',
                            })}
                            variant="h6"
                            color="primary"
                            noWrap
                            title={data.attr(attrName)}
                          >
                            {format(data.attr(attrName))}
                          </Typography>
                        );
                    })}
                  </div>
                );
              })
            }
          </div>
        );
      })}
    </Box>
  );
}

ChartLegend.defaultProps = {
  className: '',
  accountCollection: fr.collection([]),
  noColumnHeaders: false,
  usePercentage: false,
  noBreakdowns: false,
  sectionLabels: false,
};

ChartLegend.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  sections: PropTypes.array.isRequired,
  accountCollection: PropTypes.object,
  noColumnHeaders: PropTypes.bool,
  usePercentage: PropTypes.bool,
  noBreakdowns: PropTypes.bool,
  sectionLabels: PropTypes.bool,
};

export default withStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 5,
  },
  sectionRoot: {
    display: 'flex',
    flexDirection: 'column',
  },
  cellContainer: {
    display: 'flex',
    width: '20%',
  },
  headerRow: {
    padding: theme.spacing(0, 1, 1, 1),
    display: 'flex',
  },
  header: {
    color: theme.palette.facet.accentBlue2,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.h5.fontSize,
  },
  cellText: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.h5.fontSize,
  },
  tickerCellText: {
    paddingLeft: theme.spacing(1),
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.h5.fontSize,
  },
  alignTextEnd: {
    textAlign: 'end',
  },
  cell: {
    flexGrow: 1,
    // Change back to 1/6 when the Cost Basis and Gain/Loss columns are restored
    width: `${(1 / 4) * 100}%`,
    maxWidth: `${(1 / 4) * 100}%`,
    padding: theme.spacing(0, 0.5),
  },
  tableRow: {
    padding: theme.spacing(1, 1),
    display: 'flex',
  },
  sectionWithoutBreakdown: {
    paddingBottom: theme.spacing(1),
  },
  [theme.breakpoints.down('md')]: {
    header: {
      fontSize: theme.typography.h6.fontSize,
    },
    cellText: {
      fontSize: theme.typography.h6.fontSize,
    },
    tickerCellText: {
      fontSize: theme.typography.h6.fontSize,
    },
  },
}))(ChartLegend);
